// src/components/Loading.js
import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Wheat } from 'lucide-react';

/**
 * The wait states, in one place so they all look like the same site.
 *
 * Olive on cream reads as olive, but sinks into the dark surface, so the ring
 * lightens with the mode — the same rule the header accent follows.
 */
const useAccent = () => {
  const theme = useTheme();
  return theme.palette.primary.main;
};

/** The spinner itself: an olive ring turning around the brand's wheat mark. */
export const BrandSpinner = ({ size = 56 }) => {
  const accent = useAccent();

  return (
    <Box
      role="progressbar"
      aria-label="Loading"
      sx={{ position: 'relative', width: size, height: size, display: 'grid', placeItems: 'center' }}
    >
      {/* The unfilled track, so the ring reads as a ring rather than a comet. */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={2.4}
        sx={{ position: 'absolute', color: accent, opacity: 0.16 }}
      />
      <CircularProgress
        size={size}
        thickness={2.4}
        sx={{ position: 'absolute', color: accent }}
      />
      <Box
        sx={{
          display: 'flex',
          color: accent,
          animation: 'ndzBreathe 1.9s ease-in-out infinite',
          '@keyframes ndzBreathe': {
            '0%, 100%': { opacity: 0.45, transform: 'scale(0.94)' },
            '50%': { opacity: 1, transform: 'scale(1)' },
          },
        }}
      >
        <Wheat size={Math.round(size * 0.42)} strokeWidth={1.9} />
      </Box>
    </Box>
  );
};

/**
 * A whole-screen wait, for the moments before a page can decide what it is —
 * an auth check, a permissions read. It paints the themed background itself,
 * because at this point no page chrome has rendered to paint it.
 */
export const PageLoader = ({ message = 'Loading…' }) => (
  <Box
    sx={{
      minHeight: '100vh',
      px: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
      textAlign: 'center',
      backgroundColor: 'background.default',
    }}
  >
    <BrandSpinner size={64} />
    <Typography
      sx={{
        color: 'text.secondary',
        fontWeight: 600,
        fontSize: '0.95rem',
        letterSpacing: '0.02em',
      }}
    >
      {message}
    </Typography>
  </Box>
);

export default PageLoader;
